import axios from 'axios';
import { useQuery, useMutation } from 'react-query';
import userCategory from '../../../constants/userCategory';

export const useFetchClientInvoices = (clientId, token) => {
    return useQuery(['clientInvoices', token, clientId], async () => {
        const response = await axios.get(`/api/client/invoices/${clientId}`, {
            headers: {
                Authorization: `Bearer ${token}`,
            },
        });
        return response.data;
    }, {
        staleTime: 3 * 60 * 1000,
        cacheTime: 10 * 60 * 1000,
        refetchOnWindowFocus: false,
    });
};

export const useFetchClientExpenses = (clientId, token) => {
    return useQuery(['clientExpenses', token, clientId], async () => {
        const response = await axios.get(`/api/client/expenses/${clientId}`, {
            headers: {
                Authorization: `Bearer ${token}`,
            },
        });
        return response.data;
    });
};

export const useFetchClientsInvoices = ({ 
    token, 
    page = 1, 
    limit = 10, 
    invoiceNumber = '', 
    status = 'All', 
    paymentStatus = 'All', 
    dueDate = null, 
    clients = 'All' 
}) => {
    return useQuery(
        ['clientsInvoices', token, { page, limit, invoiceNumber, status, paymentStatus, dueDate, clients }],
        async () => {
            const params = new URLSearchParams();
            params.append('page', page);
            params.append('limit', limit);
            if (invoiceNumber) params.append('invoiceNumber', invoiceNumber);
            if (status !== 'All') {
                if (Array.isArray(status)) {
                    status.forEach(s => params.append('status', s));
                } else {
                    params.append('status', status);
                }
            }
            if (paymentStatus !== 'All') {
                if (Array.isArray(paymentStatus)) {
                    paymentStatus.forEach(ps => params.append('paymentStatus', ps));
                } else {
                    params.append('paymentStatus', paymentStatus);
                }
            }
            if (dueDate) params.append('dueDate', dueDate);
            if (clients !== 'All') {
                if (Array.isArray(clients)) {
                    clients.forEach(c => params.append('clients', c));
                } else {
                    params.append('clients', clients);
                }
            }

            const response = await axios.get(`/api/clients/invoices?${params.toString()}`, {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            });
            return response.data;
        },
        {
            staleTime: 5 * 60 * 1000,
            cacheTime: 10 * 60 * 1000,
            keepPreviousData: true,
            refetchOnWindowFocus: false,
            refetchOnMount: false,
            enabled: !!token,
        }
    );
};

export function useFetchClientProjectDetails(clientId, token) {
    return useQuery(['clientProjectDetails', token, clientId], async () => {
        const response = await axios.get(`/api/project-details/client/${clientId}`, {
            headers: {
                Authorization: `Bearer ${token}`,
            },
        });
        return response.data;
    }, {
        staleTime: 3 * 60 * 1000,
        cacheTime: 10 * 60 * 1000,
        refetchOnWindowFocus: false,
    });
}

export function useFetchClientSprintDetails(clientId, token) {
    return useQuery(['clientSprintDetails', token, clientId], async () => {
        const response = await axios.get(`/api/sprint-details/client/${clientId}`, {
            headers: {
                Authorization: `Bearer ${token}`,
            },
        });
        return response.data;
    });
}

export function useFetchClientsProjects(token) {
    return useQuery(['clientProjectDetails', token], async () => {
        const response = await axios.get(`/api/projects/clients`, {
            headers: {
                Authorization: `Bearer ${token}`,
            },
        });
        return response.data;
    });
}

export const useVendorUser = (token) => {
    return useQuery(['vendorUser', token], async () => {
        const response = await axios.get(`/api/user/user-vendors`, {
            headers: {
                Authorization: `Bearer ${token}`,
            },
        });
        const sortedTeams = [...response.data].sort((a, b) => {
            const nameA = a.first_name ? `${a.first_name} ${a.middle_name ? a.middle_name : ''} ${a.last_name}` : `${a.company_first_name ? a.company_first_name + (a.company_last_name && a.company_last_name) : a.company_name}`;
            const nameB = b.first_name ? `${b.first_name} ${b.middle_name ? b.middle_name : ''} ${b.last_name}` : `${b.company_first_name ? b.company_first_name + (b.company_last_name && b.company_last_name) : b.company_name}`;
            return nameA.localeCompare(nameB);
        });

        return sortedTeams;
    });
};

export const useWxCompany = (token) => {
    return useQuery(['wxCompany', token], async () => {
        const response = await axios.get(`/api/get-wx-comapies`, {
            headers: {
                Authorization: `Bearer ${token}`,
            },
        });
        const sortedWxCompany = response.data.filter(
            (compny) =>
                compny.wx_company !== "Any"
        );

        return sortedWxCompany;
    });
};